/**
 * lazyWithRetry — chunk 로드 실패 자동 복구 유틸.
 *
 * 배경
 * - Vite 빌드 시 lazy import는 해시가 붙은 청크 파일로 분리됨
 *   (예: AdminInquiriesPage-BnWpjhr8.js)
 * - 새 배포 후 사용자의 브라우저가 구 index.html을 캐시한 채로
 *   동적 import를 시도하면 구 청크 URL이 404가 되어
 *   "Failed to fetch dynamically imported module" 에러가 발생함.
 *
 * 동작
 * 1) 최초 import 실패 시 짧은 지연 후 1회 재시도 (네트워크 일시 오류 보정).
 * 2) 그래도 실패하면 chunk 미존재 가능성이 높으므로
 *    sessionStorage 플래그로 1회만 자동 새로고침을 시도하여
 *    최신 index.html을 받아오도록 한다 (무한 새로고침 방지).
 * 3) 새로고침 후에도 실패하면 그대로 에러를 throw하여
 *    상위 ErrorBoundary가 사용자에게 안내 UI를 보여주도록 한다.
 */
import { lazy, type ComponentType } from 'react'

const RELOAD_FLAG_KEY = 'cb:chunk-reload-attempted'

/**
 * sessionStorage 플래그를 안전하게 읽고 쓰는 헬퍼.
 * SSR/시크릿 모드 등에서 storage 접근이 막혔을 때 예외를 흡수한다.
 */
function safeStorage() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  const name = error instanceof Error ? error.name : ''
  // Vite/webpack 양쪽 메시지 패턴을 광범위하게 매칭
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}

/**
 * React.lazy 래퍼 — 동적 import 실패 시 재시도 + 1회 자동 reload.
 *
 * @param factory 동적 import 함수 (예: () => import('@/pages/MyPage'))
 * @param retryDelayMs 1차 실패 후 재시도까지 대기 시간 (기본 600ms)
 * @returns React.lazy 컴포넌트
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retryDelayMs = 600
) {
  return lazy(async () => {
    try {
      return await factory()
    } catch (firstError) {
      // 1차 재시도 (네트워크 일시 오류 대비)
      try {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs))
        return await factory()
      } catch (secondError) {
        // chunk 누락으로 추정되면 한 번만 새로고침
        if (isChunkLoadError(secondError) || isChunkLoadError(firstError)) {
          const storage = safeStorage()
          const alreadyReloaded = storage?.getItem(RELOAD_FLAG_KEY) === '1'

          if (!alreadyReloaded && typeof window !== 'undefined') {
            storage?.setItem(RELOAD_FLAG_KEY, '1')
            // location.reload는 캐시 우회를 보장하지 않으므로
            // index.html 자체를 다시 받기 위해 현재 URL로 강제 이동
            // (브라우저가 index.html을 재요청 → 새 청크 해시를 얻게 됨)
            window.location.reload()
            // reload는 비동기이므로 후속 코드 실행 방지용 pending Promise 반환
            return new Promise<{ default: T }>(() => {})
          }
        }
        // 새로고침해도 실패했거나 chunk 에러가 아니면 그대로 throw
        throw secondError
      }
    }
  })
}

/**
 * 새로고침 성공 후 (정상 hydrate 완료 시점에) 호출하면
 * 다음 chunk 실패 때 다시 한 번 reload를 시도할 수 있도록 플래그를 비움.
 */
export function clearChunkReloadFlag(): void {
  safeStorage()?.removeItem(RELOAD_FLAG_KEY)
}
